import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';

/*
 * Transient confirmations with an optional Undo.
 *
 * This is the safety net that makes optimistic writes acceptable: the UI
 * commits immediately, and the toast is where the change can be taken back.
 * Destructive transitions still get a real confirmation dialog — an undo window
 * is not a substitute for one when the action is irreversible server-side.
 *
 * Generalised from the pattern in CatalogWorkbench so the two screens can't
 * drift; Catalog should adopt this and delete its private copy.
 */

const DEFAULT_DURATION = 6000;
const MAX_VISIBLE = 3;

export const useToasts = ({ duration = DEFAULT_DURATION } = {}) => {
  const [toasts, setToasts] = useState([]);
  const seqRef = useRef(0);
  const timersRef = useRef(new Map());

  const dismiss = useCallback((id) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (message, { undo, tone = 'default' } = {}) => {
      const id = ++seqRef.current;
      setToasts((current) => [
        ...current.slice(-(MAX_VISIBLE - 1)),
        { id, message, undo, tone },
      ]);
      timersRef.current.set(
        id,
        setTimeout(() => dismiss(id), duration),
      );
      return id;
    },
    [dismiss, duration],
  );

  // Clear pending timers on unmount so a dismissed screen can't setState.
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach(clearTimeout);
      timers.clear();
    };
  }, []);

  return { toasts, push, dismiss };
};

const TONE_CLASS = {
  default: 'bg-nysmah-dark text-white',
  danger: 'bg-[#B93A33] text-white',
};

export const ToastStack = ({ toasts, onDismiss }) => {
  if (!toasts.length) return null;

  return (
    <div
      className="fixed left-1/2 bottom-6 z-[1400] flex -translate-x-1/2 flex-col gap-2 w-max max-w-[92vw]"
      role="status"
      aria-live="polite"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`flex items-center gap-3 rounded-lg px-4 py-2.5 text-[13px] shadow-lg ${
            TONE_CLASS[toast.tone] || TONE_CLASS.default
          }`}
        >
          <span>{toast.message}</span>
          {toast.undo && (
            <button
              type="button"
              className="bg-transparent border-0 p-0 font-semibold text-white underline underline-offset-2 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 rounded"
              onClick={() => {
                onDismiss(toast.id);
                toast.undo();
              }}
            >
              Undo
            </button>
          )}
          <button
            type="button"
            className="bg-transparent border-0 p-0 text-white/70 hover:text-white cursor-pointer inline-flex focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 rounded"
            onClick={() => onDismiss(toast.id)}
            aria-label="Dismiss"
          >
            <X size={13} aria-hidden="true" />
          </button>
        </div>
      ))}
    </div>
  );
};

export default useToasts;
